import { MAX_FILES_PER_UPLOAD, MAX_UPLOAD_BYTES, MAX_UPLOAD_MB } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { FileDropzone, Icon, Skeleton, type FileRejection } from "@sagot/ui";
import type { useUploads } from "../../features/deploy-knowledge/knowledge/use-uploads";
import type { SourceTargets } from "./use-source-targets";

const KB_ADD = "deployKnowledge.knowledge.add";

interface FilesTabProps {
  uploads: ReturnType<typeof useUploads>;
  targets: SourceTargets;
  /** Template wording for what not to upload (e.g. "learner names and LRNs"). */
  sensitive: string;
}

/**
 * Upload files: picking or dropping starts the real presigned upload right away (presign → PUT →
 * finalize). Progress, processing and failures are listed under the tabs (`SourcesList`).
 */
export function FilesTab({ uploads, targets, sensitive }: FilesTabProps) {
  const { t } = useTranslation();
  const rejected = ({ file, reason }: FileRejection) =>
    ({
      type: t(`${KB_ADD}.rejectType`, { name: file.name }),
      size: t(`${KB_ADD}.rejectSize`, { name: file.name, mb: MAX_UPLOAD_MB }),
      empty: t(`${KB_ADD}.rejectEmpty`, { name: file.name }),
      count: t(`${KB_ADD}.rejectCount`, { name: file.name, max: MAX_FILES_PER_UPLOAD }),
    })[reason];
  if (!targets.ready) return <Skeleton data-testid="files-loading" className="h-40 w-full" />;
  return (
    <div className="space-y-4">
      <FileDropzone
        maxBytes={MAX_UPLOAD_BYTES}
        maxFiles={MAX_FILES_PER_UPLOAD}
        onFiles={(files) => void uploads.start(files, targets.audiences, targets.agentIds)}
        labels={{ title: t("onboarding.knowledge.dropTitle"), hint: t("onboarding.knowledge.dropHint"), dragActive: t(`${KB_ADD}.dropActive`), rejected }}
      />
      <p className="flex items-start gap-2 text-xs text-fg-subtle">
        <Icon name="shield-check" className="mt-0.5 size-3.5 shrink-0" />
        {t("onboarding.knowledge.filesNote", { sensitive })}
      </p>
      <p className="flex items-start gap-2 text-xs text-fg-subtle">
        <Icon name="scan-text" className="mt-0.5 size-3.5 shrink-0" />
        {t(`${KB_ADD}.ocrNote`)}
      </p>
    </div>
  );
}
